import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UserMinus, XCircle, Printer } from "lucide-react";
import { getGradeData } from "../gradedata/gradedata";
import "./AdmissionList.css";
import AadyaLogoMain from "../Images/AadyaLogoMain.png";

const API_BASE_URL = "https://aadyaacademy.org/api";

function AdmissionList() {
  const [admissionList, setAdmissionList] = useState([]);
  const [enquiryList, setEnquiryList] = useState([]);
  const [filteredAdmissions, setFilteredAdmissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [academicYearFilter, setAcademicYearFilter] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForPrint, setSelectedForPrint] = useState(null);

  const fetchAdmissions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admissions/list?status=Active`);
      const data = await response.json();
      const rawData = Array.isArray(data) ? data : (data.data || []);
      setAdmissionList(rawData);
      const years = [...new Set(rawData.map(a => a.academic_year_joined).filter(Boolean))];
      setAcademicYearOptions(years);
      if (years.length > 0) setAcademicYearFilter(years[0]);
      const grades = [...new Set(rawData.map(a => String(a.class || "")).filter(Boolean))].sort((a,b) => a-b);
      setGradeOptions(grades);
    } catch (err) { toast.error("Failed to load admissions"); } finally { setLoading(false); }
  };

  const fetchEnquiries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/enquiries/list`);
      const data = await response.json();
      setEnquiryList(Array.isArray(data) ? data : (data.data || []));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAdmissions(); fetchEnquiries(); }, []);

  useEffect(() => {
    let result = admissionList.filter(a => 
      (!searchTerm || (a.enquiry_name || "").toLowerCase().includes(searchTerm.toLowerCase())) &&
      (!academicYearFilter || a.academic_year_joined === academicYearFilter) &&
      (!gradeFilter || String(a.class || "") === gradeFilter)
    );
    setFilteredAdmissions(result);
  }, [searchTerm, academicYearFilter, gradeFilter, admissionList]);

  const handlePrintAdmitCard = (adm) => {
    const student = enquiryList.find(e => e.enquiry_name === adm.enquiry_name) || {};
    const gradeData = getGradeData(adm.class || "1");
    setSelectedForPrint({ admission: adm, student, gradeData });
    setTimeout(() => { window.print(); }, 300);
  };

  const handleWithdraw = async (adm) => {
    if (!window.confirm(`Are you sure you want to withdraw ${adm.enquiry_name}?`)) return;
    try {
      const id = adm.admission_id || adm.id;
      await fetch(`${API_BASE_URL}/enquiries/update/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enquiry_status: "Withdrawn" }),
      });
      toast.warning("Admission Withdrawn");
      fetchAdmissions();
    } catch (err) { toast.error("Error updating status"); }
  };

  const handleCancel = async (adm) => {
    const reason = window.prompt("Reason for Cancellation?");
    if (!reason) return;
    try {
      const id = adm.admission_id || adm.id;
      await fetch(`${API_BASE_URL}/enquiries/update/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enquiry_status: "Cancelled", remarks: reason }),
      });
      toast.warning("Admission Cancelled");
      fetchAdmissions();
    } catch (err) { toast.error("Error updating status"); }
  };

  return (
    <>
      <div className="admissions-container no-print">
        <h2>ADMISSIONS LIST ({filteredAdmissions.length})</h2>
        <div className="filters">
          <input 
            type="text" 
            placeholder="Search by Name..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
          <select value={academicYearFilter} onChange={(e) => setAcademicYearFilter(e.target.value)}>
             {academicYearOptions.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
          <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}>
             <option value="">All Grades</option>
             {gradeOptions.map(grade => <option key={grade} value={grade}>Grade {grade}</option>)}
          </select>
        </div>

        {loading ? <p>Loading...</p> : (
          <table className="admission-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Grade</th>
                <th>Year</th>
                <th className="action-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmissions.map((adm, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{adm.enquiry_name}</td>
                  <td>{adm.class}</td>
                  <td>{adm.academic_year_joined}</td>
                  <td>
                    <div className="action-buttons">
                        <button className="btn withdraw-btn" onClick={() => handleWithdraw(adm)} title="Withdraw"><UserMinus size={18}/></button>
                        <button className="btn cancel-btn" onClick={() => handleCancel(adm)} title="Cancel"><XCircle size={18}/></button>
                        <button className="btn print-btn" onClick={() => handlePrintAdmitCard(adm)} title="Print"><Printer size={18}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedForPrint && (
        <div className="print-content-only">
          <div className="page-a4-fixed">
            <div className="top-orange-line" />
            
            <div className="admit-header-box">
              <div className="header-logo-side">
                <img src={AadyaLogoMain} className="logo-img-admit" alt="Logo"/>
              </div>
              <div className="header-info-side">
                <div className="info-top-row">
                  <span className="blue-italic-text">Can Admit</span>
                  <span className="bold-grade-val">{selectedForPrint.admission.class}</span>
                  <span className="bold-icse-label">ICSE</span>
                  <span className="bold-student-name-header">{selectedForPrint.admission.enquiry_name}</span>
                </div>
                <div className="info-detail-grid">
                  <span>Father/Guardian:</span>
                  <span>{selectedForPrint.student.father_name || "—"}</span>
                  <span className="detail-year-red">{selectedForPrint.admission.academic_year_joined}</span>
                  <div className="detail-address-text">{selectedForPrint.student.address || "Bangalore"}</div>
                </div>
              </div>
              <div className="header-photo-side">
                <div className="photo-placeholder-box">STUDENT PHOTO</div>
              </div>
            </div>

            <div className="header-divider-line" />

            <div className="main-content-area">
              <div className="notice-section">
                <p><strong>Dear Parents,</strong></p>
                <p>We request you kindly make note of the following:-</p>
                <p>I: School Re-open Date:- <strong>22nd May 2025, Thursday</strong></p>
                <p>II: Books Stationery (Material) - Distribution: <strong>22nd March / 3rd April / 4th April</strong></p>
              </div>

              {/* Material List Section starts here with a defined gap */}
              <div className="material-list-wrapper">
                 <p className="material-list-title">Grade {selectedForPrint.admission.class} - Material List</p>
                 <div className="material-columns-container">
                    <div className="column-left">
                      <div className="grade-list-section">
                        <p className="list-section-header">Text Books List</p>
                        <ul className="print-ul-items">
                          {selectedForPrint.gradeData.textBooks?.map((book, i) => <li key={i}>{book}</li>)}
                        </ul>
                      </div>
                      <div className="grade-list-section">
                        <p className="list-section-header">Note Books List</p>
                        <div className="item-row-layout">
                           {selectedForPrint.gradeData.noteBooks?.map((nb, i) => (
                             <div key={i} className="plain-data-row">
                               <span>• {nb.name}</span>
                               <span>{nb.pages} Pages</span>
                               <span>— {nb.qty}</span>
                             </div>
                           ))}
                        </div>
                      </div>
                    </div>

                    <div className="column-right">
                      <div className="grade-list-section">
                        <p className="list-section-header">Additional Books List</p>
                        <ul className="print-ul-items">
                          <li>* National Geographic Book</li>
                          <li>* Art & Craft – My Art My Way</li>
                          <li>* Health Education Book</li>
                        </ul>
                      </div>
                      <div className="grade-list-section">
                        <p className="list-section-header">Stationery List</p>
                        <div className="item-row-layout">
                           {selectedForPrint.gradeData.stationery?.map((st, i) => (
                             <div key={i} className="plain-data-row">
                               <span>* {st.item}</span>
                               <span>— {st.qty}</span>
                             </div>
                           ))}
                        </div>
                      </div>
                      <div className="grade-list-section">
                        <p className="list-section-header">Uniform</p>
                        <div className="item-row-layout">
                            <div className="plain-data-row"><span>• Regular Uniform</span><span>— 2 Pairs</span></div>
                            <div className="plain-data-row"><span>• School wear</span><span>— 2 Pairs</span></div>
                            <div className="plain-data-row"><span>• Belt / Socks / Tie</span><span>— 1 Each</span></div>
                        </div>
                      </div>
                    </div>
                 </div>
              </div>

              <div className="fee-structure-block">
                <p className="discretion-note">Note: Particulars and quantity may change on discretion of the management.</p>
                <p className="van-info">III. For school Van Facility please collect 'Van Facility Form'.</p>
                <p className="term-info">IV. Term Fee Structure:- <strong>{selectedForPrint.admission.academic_year_joined}</strong></p>

                <table className="fee-table-print">
                  <thead>
                    <tr><th>Terms</th><th>Amount</th><th>Pay By</th></tr>
                  </thead>
                  <tbody>
                    {selectedForPrint.gradeData.fee?.map((fee, i) => (
                      <tr key={i}>
                        <td>{fee.term}</td>
                        <td>{fee.amount}</td>
                        <td>{fee.payBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="thank-you-wrapper">
                  <p className="association-note">Looking forward to further association.</p>
                  <p className="thank-you-note">Thanking You</p>
                </div>
              </div>
            </div>

            <div className="signature-row-frames">
              <div className="sig-frame-item"><span>Managing Director</span></div>
              <div className="sig-frame-item"><span>Accounts</span></div>
              <div className="sig-frame-item"><span>Uniform Measurement</span></div>
            </div>

            <div className="footer-contact-info">
              <div className="bottom-orange-line" />
              <p>#121/1, Vinayaka Layout, Kannur, Bangalore - 560077</p>
              <p>{`Ph: 9963604909, 8904732323 | Email: aadyaacademykannur@gmail.com | www.aadyaacademy.com`}</p>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
}

export default AdmissionList;